import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa6";

const TableSearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  name,
  disabled = false,
  optionKey = "value",
  optionLabel = "label",
  noOptionsMessage = "No options found",
  index,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState('above');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Handle options that are strings or objects
  const normalizeOptions = (opts) => {
    return opts.map((opt) => {
      if (typeof opt === "string") {
        return { value: opt, label: opt };
      }
      return {
        value: opt[optionKey],
        label: opt[optionLabel] || opt[optionKey],
      };
    });
  };

  const normalizedOptions = normalizeOptions(options);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(normalizedOptions);
    } else {
      const filtered = normalizedOptions.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Calculate dropdown position based on available space
  const calculateDropdownPosition = () => {
    if (!dropdownRef.current) return 'below';
    
    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownHeight = 240; // max-h-60 = 240px approximately
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // For mobile devices or when there's insufficient space below
    if (window.innerWidth < 768 || spaceBelow < dropdownHeight) {
      // Check if there's more space above
      if (spaceAbove > spaceBelow && spaceAbove > 200) {
        return 'above';
      }
    }
    
    return 'below';
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const position = calculateDropdownPosition();
      setDropdownPosition(position);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get display value
  const getDisplayValue = () => {
    if (!value) return "";
    const selectedOption = normalizedOptions.find((opt) => opt.value === value);
    return selectedOption ? selectedOption.label : value;
  };

  const handleOptionSelect = (selectedValue) => {
    onChange(index, name, selectedValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Calculate dropdown styles based on position
  const getDropdownStyles = () => {
    if (!dropdownRef.current) return {};
    
    const rect = dropdownRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    
    if (dropdownPosition === 'above') {
      return {
        bottom: isMobile 
          ? window.innerHeight - rect.top + 2
          : window.innerHeight - rect.top - window.scrollY + 2,
        left: rect.left + (isMobile ? 0 : window.scrollX),
        width: rect.width,
      };
    } else {
      return {
        top: isMobile 
          ? rect.bottom - 2
          : rect.bottom + window.scrollY + 4,
        left: rect.left + (isMobile ? 0 : window.scrollX),
        width: rect.width,
      };
    }
  };

  return (
    <div
      className={`relative ${disabled ? "opacity-50" : ""}`}
      ref={dropdownRef}
    >
      {/* Main select field styled like table dropdown */}
      <div className="relative borderless-dropdown">
        <div
          className={`
            appearance-none border-gray-300 rounded w-full py-2 px-3 pr-8 text-xs focus:outline-none cursor-pointer flex justify-between items-center
            ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}
          `}
          onClick={handleInputClick}
        >
          <span
            className={`truncate ${
              getDisplayValue() ? "text-text2" : "text-gray-400"
            }`}
          >
            {getDisplayValue() || placeholder}
          </span>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <FaChevronDown
              className={`transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`
            w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden min-w-[200px]
            md:fixed md:z-[9999] sm:absolute z-[9999]
            ${dropdownPosition === 'above' ? 'origin-bottom' : 'origin-top'}
          `}
          style={getDropdownStyles()}
        >
          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map((option, optionIndex) => (
                <div
                  key={`${option.value}-${optionIndex}`}
                  className={`
                    px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors
                    ${
                      option.value === value
                        ? "bg-blue-50 text-blue-600"
                        : "text-text2"
                    }
                  `}
                  onClick={() => handleOptionSelect(option.value)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>

          {/* Search input */}
          <div className="p-2 border-t border-gray-200">
            <input
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TableSearchableDropdown;